// =============================================================================
// Defaults
// =============================================================================
export const DEFAULT_VM_SIZE = 'medium';
/** Default VM location (Hetzner). Provider-specific defaults come from the provider catalog. */
export const DEFAULT_VM_LOCATION = 'nbg1';
export const DEFAULT_BRANCH = 'main';
export const DEFAULT_WORKSPACE_PROFILE = 'full';
export const VALID_WORKSPACE_PROFILES = ['full', 'lightweight'];
/**
 * Regex for valid devcontainer config names (directory names under .devcontainer/).
 * Allows alphanumeric characters, hyphens, and underscores.
 * Override via DEVCONTAINER_CONFIG_NAME_PATTERN env var.
 */
export const DEVCONTAINER_CONFIG_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;
/** Maximum length for devcontainer config names. Override via DEVCONTAINER_CONFIG_NAME_MAX_LENGTH env var. */
export const DEVCONTAINER_CONFIG_NAME_MAX_LENGTH = 128;
// =============================================================================
// Default Limits (configurable via environment variables)
// Per constitution principle XI: all values must be configurable
// =============================================================================
/** Default max nodes per user. Override via MAX_NODES_PER_USER env var. */
export const DEFAULT_MAX_NODES_PER_USER = 10;
/** Default max agent sessions per workspace. Override via MAX_AGENT_SESSIONS_PER_WORKSPACE env var. */
export const DEFAULT_MAX_AGENT_SESSIONS_PER_WORKSPACE = 10;
/** Default node heartbeat staleness threshold in seconds. Override via NODE_HEARTBEAT_STALE_SECONDS env var. */
export const DEFAULT_NODE_HEARTBEAT_STALE_SECONDS = 180;
/** Default timeout for task-liveness VM-agent health probes. Override via TASK_LIVENESS_NODE_HEALTH_PROBE_TIMEOUT_MS env var. */
export const DEFAULT_TASK_LIVENESS_NODE_HEALTH_PROBE_TIMEOUT_MS = 5_000;
/** Default max projects per user. Override via MAX_PROJECTS_PER_USER env var. */
export const DEFAULT_MAX_PROJECTS_PER_USER = 100;
/** Default max tasks per project. Override via MAX_TASKS_PER_PROJECT env var. */
export const DEFAULT_MAX_TASKS_PER_PROJECT = 10_000;
/** Default max dependency edges per task. Override via MAX_TASK_DEPENDENCIES_PER_TASK env var. */
export const DEFAULT_MAX_TASK_DEPENDENCIES_PER_TASK = 50;
/** Default task list page size. Override via TASK_LIST_DEFAULT_PAGE_SIZE env var. */
export const DEFAULT_TASK_LIST_DEFAULT_PAGE_SIZE = 50;
/** Default max task list page size. Override via TASK_LIST_MAX_PAGE_SIZE env var. */
export const DEFAULT_TASK_LIST_MAX_PAGE_SIZE = 200;
/**
 * Default message limit for chat session REST endpoints (project chat view).
 * Streaming-token chat messages produce many more DB rows than logical messages,
 * so this limit is higher than SAM_HISTORY_LOAD_LIMIT (200).
 *
 * Kept well below the Cloudflare DO RPC serialization ceiling (32 MiB) —
 * large tool-call output can easily push 3 000 rows past the limit.
 * The frontend already paginates via the `before` / `hasMore` contract.
 * Override via CHAT_SESSION_MESSAGE_LIMIT env var.
 */
export const DEFAULT_CHAT_SESSION_MESSAGE_LIMIT = 500;
/**
 * Ceiling (max clamp) for a single chat session message REST response.
 *
 * This is distinct from DEFAULT_CHAT_SESSION_MESSAGE_LIMIT, the page size used
 * when no explicit limit is requested. The project chat no longer asks for this
 * ceiling: it opens a session on its newest page and pages older history in as
 * the reader scrolls up, and a jump to an unloaded message pages back until the
 * message is loaded. The ceiling bounds whatever an API caller does request.
 *
 * Compact-mode token rows are small (~150 B), so even the largest observed
 * production sessions (~30k token rows ≈ ~4.5 MB) fit in one response and stay
 * well under the Cloudflare DO 30 MiB RPC size guard in `getMessages()`.
 * Responses larger than the ceiling OR the RPC size guard keep `hasMore=true`.
 *
 * Override via CHAT_SESSION_MESSAGE_MAX env var.
 */
export const DEFAULT_CHAT_SESSION_MESSAGE_MAX = 50000;
/**
 * Default page size for forward-cursor chat delta fetches.
 *
 * Browser-side cached conversations only need rows newer than the newest cached
 * message during background revalidation. Keep this configurable separately from
 * the newest-page initial load so operators can tune the catch-up payload
 * without changing how much a cold open reads.
 *
 * Override via CHAT_SESSION_DELTA_MESSAGE_LIMIT env var.
 */
export const DEFAULT_CHAT_SESSION_DELTA_MESSAGE_LIMIT = 5000;
/**
 * Safety bound on how many forward pages one chat delta may read.
 *
 * A delta drains every message newer than the cached transcript, page by page,
 * so a tab that fell far behind catches up without a gap. At the default delta
 * page size this covers more messages than a session can hold; the bound exists
 * only so a server that never clears `hasMore` cannot spin the client.
 *
 * Override at build time via VITE_CHAT_DELTA_MAX_PAGES.
 */
export const DEFAULT_CHAT_DELTA_MAX_PAGES = 50;
// =============================================================================
// Message-anchored comments
// =============================================================================
/** Max comment thread/reply body length. Override via COMMENT_BODY_MAX_LENGTH. */
export const DEFAULT_COMMENT_BODY_MAX_LENGTH = 8_000;
/** Max selected quote snapshot length stored on message anchors. Override via COMMENT_QUOTE_MAX_LENGTH. */
export const DEFAULT_COMMENT_QUOTE_MAX_LENGTH = 2_000;
/** Max idempotency key length accepted for optimistic comment mutations. Override via COMMENT_IDEMPOTENCY_KEY_MAX_LENGTH. */
export const DEFAULT_COMMENT_IDEMPOTENCY_KEY_MAX_LENGTH = 200;
/** Default page size for session comment list reads. Override via COMMENT_LIST_LIMIT_DEFAULT. */
export const DEFAULT_COMMENT_LIST_LIMIT_DEFAULT = 100;
/** Max page size for session comment list reads. Override via COMMENT_LIST_LIMIT_MAX. */
export const DEFAULT_COMMENT_LIST_LIMIT_MAX = 500;
/** Max message-anchored comment threads per chat session. Override via COMMENT_THREADS_PER_SESSION_MAX. */
export const DEFAULT_COMMENT_THREADS_PER_SESSION_MAX = 1_000;
/** Max replies per comment thread. Override via COMMENT_REPLIES_PER_THREAD_MAX. */
export const DEFAULT_COMMENT_REPLIES_PER_THREAD_MAX = 200;
/**
 * How many threads `GET /api/projects/:projectId/comments` returns in one page.
 *
 * The project comment inbox is a triage surface, not an archive: it answers "is
 * anything waiting on me", and nobody triages more than a screenful. The cap is
 * ranked by `updated_at DESC` — most recently active first — because that is
 * what a reader of an inbox needs, and the response carries `totalCount` so a
 * truncated list can never be mistaken for a complete one (rule 65).
 *
 * Override via PROJECT_COMMENT_LIST_LIMIT.
 */
export const DEFAULT_PROJECT_COMMENT_LIST_LIMIT = 100;
/**
 * Ceiling on a caller-supplied `limit` for the project comment inbox.
 *
 * Bounds the work the Durable Object does per request and the size of the RPC
 * payload it returns, since threads are hydrated with their replies.
 *
 * Override via PROJECT_COMMENT_LIST_MAX.
 */
export const DEFAULT_PROJECT_COMMENT_LIST_MAX = 300;
/**
 * Content-byte budget for one project-wide comment inbox response.
 *
 * The DO applies this before hydrating replies so a small number of very long
 * threads cannot exceed Cloudflare's Durable Object RPC payload ceiling or
 * consume the shared 128 MB isolate. This is an estimate over comment body,
 * quote, and reply body bytes; JSON/object overhead stays bounded by the row
 * count caps above.
 *
 * Override via PROJECT_COMMENT_LIST_MAX_BYTES.
 */
export const DEFAULT_PROJECT_COMMENT_LIST_MAX_BYTES = 4_000_000;
/**
 * Safety bound on how many older pages the chat client will fetch while chasing a
 * timeline jump target that predates the loaded window (the rare oversized-session
 * fallback). Bounds the load-until loop so a server misreporting `hasMore` can
 * never loop unbounded. Override at build time via VITE_CHAT_LOAD_UNTIL_MAX_PAGES.
 */
export const DEFAULT_CHAT_LOAD_UNTIL_MAX_PAGES = 400;
/**
 * Cap on how many sessions the ProjectData DO mirrors into the D1
 * `session_summaries` index per project, per sync.
 *
 * A project whose session count exceeds this is marked incomplete in
 * `session_index_coverage`, and its per-project list falls back to the DO — the
 * index may only answer a read it can prove is equivalent. Sized well above the
 * sidebar's 100-row page so the fast path applies to effectively every real
 * project. Override via SESSION_INDEX_MAX_ROWS env var.
 */
export const DEFAULT_SESSION_INDEX_MAX_ROWS = 1000;
/**
 * How stale a `session_index_coverage` row may be before the per-project session
 * list stops trusting D1 and falls back to the ProjectData DO.
 *
 * The DO schedules a sync within DO_SUMMARY_SYNC_DEBOUNCE_MS of every session
 * mutation, so under normal operation the index is seconds behind. This bound is
 * not there for normal operation — it is the fail-closed backstop for the case
 * where the sync itself breaks (its D1 writes are deliberately swallowed so a
 * sync failure cannot fail the mutation, which means a broken sync is silent).
 *
 * The fallback is self-healing: the DO's listSessions RPC schedules a sync, so
 * one stale read re-primes the index for the next one. Override via
 * SESSION_INDEX_MAX_STALENESS_MS env var.
 */
export const DEFAULT_SESSION_INDEX_MAX_STALENESS_MS = 15 * 60 * 1000;
/**
 * Safety bound on how many pages the timeline drawer fetches when it opens.
 *
 * The drawer paginates user messages and progress notifications to exhaustion to
 * build a complete jump index. Both loops were previously unbounded `for(;;)`
 * loops with no page cap, so a server that keeps reporting `hasMore` (or a
 * cursor that fails to advance) would spin forever. This is the same class of
 * guard as DEFAULT_CHAT_LOAD_UNTIL_MAX_PAGES, applied per loop.
 *
 * Override at build time via VITE_CHAT_TIMELINE_MAX_PAGES.
 */
export const DEFAULT_CHAT_TIMELINE_MAX_PAGES = 200;
/**
 * Whether chat session message loads strip tool_metadata.content by default.
 * When true, tool call content is lazy-loaded on demand when users expand
 * individual tool calls, dramatically reducing RPC payload size.
 * Override via CHAT_COMPACT_MODE_DEFAULT env var.
 */
export const DEFAULT_CHAT_COMPACT_MODE = true;
/** Default callback timeout for delegated task updates in milliseconds. */
export const DEFAULT_TASK_CALLBACK_TIMEOUT_MS = 10000;
/** Default retry attempts for delegated task callback processing. */
export const DEFAULT_TASK_CALLBACK_RETRY_MAX_ATTEMPTS = 3;
/** Default max runtime env vars per project. Override via MAX_PROJECT_RUNTIME_ENV_VARS_PER_PROJECT env var. */
export const DEFAULT_MAX_PROJECT_RUNTIME_ENV_VARS_PER_PROJECT = 150;
/** Default max runtime files per project. Override via MAX_PROJECT_RUNTIME_FILES_PER_PROJECT env var. */
export const DEFAULT_MAX_PROJECT_RUNTIME_FILES_PER_PROJECT = 50;
/** Default max runtime env var value size in bytes. Override via MAX_PROJECT_RUNTIME_ENV_VALUE_BYTES env var. */
export const DEFAULT_MAX_PROJECT_RUNTIME_ENV_VALUE_BYTES = 8 * 1024;
/** Default max deployment environment config vars per environment. Override via MAX_DEPLOYMENT_ENV_VARS_PER_ENVIRONMENT env var. */
export const DEFAULT_MAX_DEPLOYMENT_ENV_VARS_PER_ENVIRONMENT = 100;
/** Default max deployment environment config value size in bytes. Override via MAX_DEPLOYMENT_ENV_VALUE_BYTES env var. */
export const DEFAULT_MAX_DEPLOYMENT_ENV_VALUE_BYTES = 64 * 1024;
/** Default aggregate deployment environment config size in bytes. Override via MAX_DEPLOYMENT_ENV_TOTAL_BYTES env var. */
export const DEFAULT_MAX_DEPLOYMENT_ENV_TOTAL_BYTES = 256 * 1024;
/** Default max runtime file content size in bytes. Override via MAX_PROJECT_RUNTIME_FILE_CONTENT_BYTES env var. */
export const DEFAULT_MAX_PROJECT_RUNTIME_FILE_CONTENT_BYTES = 128 * 1024;
/** Default max runtime file path length. Override via MAX_PROJECT_RUNTIME_FILE_PATH_LENGTH env var. */
export const DEFAULT_MAX_PROJECT_RUNTIME_FILE_PATH_LENGTH = 256;
/** Default max additional same-installation GitHub repositories per project (submodule access).
 *  Override via MAX_PROJECT_GITHUB_REPOS_PER_PROJECT env var. */
export const DEFAULT_MAX_PROJECT_GITHUB_REPOS_PER_PROJECT = 20;
/** Maximum workspace name length. */
export const WORKSPACE_NAME_MAX_LENGTH = 64;
/** Threshold (ms) after which a task is considered inactive on the dashboard. Override via DASHBOARD_INACTIVE_THRESHOLD_MS. */
export const DEFAULT_DASHBOARD_INACTIVE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
/** Default number of most recently active tasks the dashboard shows. Override via DASHBOARD_ACTIVE_TASK_LIMIT. */
export const DEFAULT_DASHBOARD_ACTIVE_TASK_LIMIT = 6;
/**
 * Default number of active tasks ranked by recency before the dashboard's display limit applies.
 * Override via DASHBOARD_ACTIVE_TASK_CANDIDATE_LIMIT.
 */
export const DEFAULT_DASHBOARD_ACTIVE_TASK_CANDIDATE_LIMIT = 100;
/** Default dashboard poll interval (ms) for active tasks. */
export const DEFAULT_DASHBOARD_POLL_INTERVAL_MS = 15_000; // 15 seconds
// =============================================================================
// MCP Token Configuration
// =============================================================================
/** Default MCP token TTL in seconds. Must be >= DEFAULT_TASK_RUN_MAX_EXECUTION_MS / 1000
 * so tokens remain valid for the full duration of a task. With sliding window refresh,
 * this is the inactivity timeout — tokens are auto-extended while in active use.
 * Override via MCP_TOKEN_TTL_SECONDS env var. */
export const DEFAULT_MCP_TOKEN_TTL_SECONDS = 8 * 60 * 60; // 8 hours (inactivity timeout with sliding window)
/** Default maximum lifetime for MCP tokens regardless of activity (hard cap).
 * Even with sliding window refresh, tokens are rejected after this absolute duration.
 * Override via MCP_TOKEN_MAX_LIFETIME_SECONDS env var. */
export const DEFAULT_MCP_TOKEN_MAX_LIFETIME_SECONDS = 24 * 60 * 60; // 24 hours
// =============================================================================
// Report Issue
// =============================================================================
/** Max length for report title. Override via REPORT_ISSUE_TITLE_MAX_LENGTH env var. */
export const DEFAULT_REPORT_ISSUE_TITLE_MAX_LENGTH = 200;
/** Max length for report description. Override via REPORT_ISSUE_DESCRIPTION_MAX_LENGTH env var. */
export const DEFAULT_REPORT_ISSUE_DESCRIPTION_MAX_LENGTH = 5_000;
/** Max length for stored report content (description + technical references). Override via REPORT_ISSUE_CONTENT_MAX_LENGTH env var. */
export const DEFAULT_REPORT_ISSUE_CONTENT_MAX_LENGTH = 65_536;
// =============================================================================
// Client Query Cache Persistence (apps/web)
// =============================================================================
// The web app persists an allowlisted slice of its TanStack Query cache to
// IndexedDB so a full page reload paints from cache instead of refetching the
// world. See apps/web/src/lib/query-persistence.ts.
/** How long a persisted query cache entry may be restored after it was written.
 * `persistQueryClientRestore` discards (and deletes) anything older. Matches
 * TanStack's own documented default. Override via VITE_QUERY_PERSIST_MAX_AGE_MS. */
export const DEFAULT_QUERY_PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
/** Minimum gap between IndexedDB writes. TanStack v5 removed `throttleTime` from
 * persistQueryClient, so the persister throttles its own writes to avoid an IDB
 * write per cache event. Override via VITE_QUERY_PERSIST_THROTTLE_MS. */
export const DEFAULT_QUERY_PERSIST_THROTTLE_MS = 1_000;
/** Upper bound on the initial cache restore for a signed-in user.
 *
 * Rendering is gated on this restore, and that gate can only ever ADD latency to
 * first paint: it runs after the session round trip (it needs the identity to
 * pick the right record), and it suppresses the spinner `ProtectedRoute` would
 * otherwise show, because that spinner lives inside the gated subtree. So the
 * budget is deliberately tight — a healthy IndexedDB read is single-digit
 * milliseconds, and past this point failing open to an empty in-memory cache
 * beats holding a blank screen.
 * Override via VITE_QUERY_PERSIST_RESTORE_TIMEOUT_MS. */
export const DEFAULT_QUERY_PERSIST_RESTORE_TIMEOUT_MS = 250;
/** How long the browser keeps a project chat transcript after it was last loaded
 * or updated, in memory and in the persisted query cache. Opening a chat inside
 * this window paints the cached transcript at once and refreshes it in the
 * background. Override via VITE_CHAT_TRANSCRIPT_CACHE_TTL_MS. */
export const DEFAULT_CHAT_TRANSCRIPT_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
/** Most chat transcripts the browser keeps at once. Opening a chat evicts the
 * least recently updated ones beyond this, which bounds memory, the persisted
 * record, and the cost of serializing that record on every write.
 * Override via VITE_CHAT_TRANSCRIPT_CACHE_MAX_SESSIONS. */
export const DEFAULT_CHAT_TRANSCRIPT_CACHE_MAX_SESSIONS = 20;
/** Newest rows of one chat transcript written to the persisted query cache. Paging
 * back can grow a transcript in memory without limit; on disk each keeps only the
 * page a cold open would load, so a persisted write stays bounded by this times
 * the transcript count. Override via VITE_CHAT_TRANSCRIPT_PERSIST_MAX_ROWS. */
export const DEFAULT_CHAT_TRANSCRIPT_PERSIST_MAX_ROWS = DEFAULT_CHAT_SESSION_MESSAGE_LIMIT;
// =============================================================================
// HTTP Response Cache-Control (apps/api)
// =============================================================================
// Conservative stale-while-revalidate budgets for stable/semi-stable GETs. See
// apps/api/src/lib/cache-headers.ts. Authenticated responses are ALWAYS `private`
// and carry `Vary: Cookie`; `public` is reserved for unauthenticated, globally
// identical responses.
/** `max-age` for unauthenticated deploy-scoped config GETs (/api/config/*).
 * Override via PUBLIC_CONFIG_CACHE_MAX_AGE_SECONDS. */
export const DEFAULT_PUBLIC_CONFIG_CACHE_MAX_AGE_SECONDS = 60;
/** `stale-while-revalidate` for unauthenticated deploy-scoped config GETs.
 * Override via PUBLIC_CONFIG_CACHE_SWR_SECONDS. */
export const DEFAULT_PUBLIC_CONFIG_CACHE_SWR_SECONDS = 300;
/** `max-age` for the authenticated but globally identical model catalog.
 *
 * Deliberately far below the KV catalog TTL (`MODEL_CATALOG_CACHE_TTL_SECONDS`,
 * default 3600) so a refreshed catalog is not masked for long. The two are
 * independent env vars and nothing enforces the ordering — raising this above the
 * KV TTL is a staleness footgun, not a caught error.
 * Override via MODEL_CATALOG_CACHE_MAX_AGE_SECONDS. */
export const DEFAULT_MODEL_CATALOG_RESPONSE_CACHE_MAX_AGE_SECONDS = 60;
/** `stale-while-revalidate` for the model catalog response.
 * Override via MODEL_CATALOG_CACHE_SWR_SECONDS. */
export const DEFAULT_MODEL_CATALOG_RESPONSE_CACHE_SWR_SECONDS = 300;
/** `max-age` for per-user project-scoped reference GETs (agent profiles, skills).
 * Deliberately 0: the response is served stale-then-revalidated rather than held
 * fresh, so a user's own edit is never masked by more than one request.
 * Override via PROJECT_REFERENCE_CACHE_MAX_AGE_SECONDS. */
export const DEFAULT_PROJECT_REFERENCE_CACHE_MAX_AGE_SECONDS = 0;
/** `stale-while-revalidate` for per-user project-scoped reference GETs.
 * Override via PROJECT_REFERENCE_CACHE_SWR_SECONDS. */
export const DEFAULT_PROJECT_REFERENCE_CACHE_SWR_SECONDS = 30;
/** Default max MCP server connections per scope (per user, and per project). Override via MAX_MCP_CONNECTIONS_PER_SCOPE env var. */
export const DEFAULT_MAX_MCP_CONNECTIONS_PER_SCOPE = 25;
/** Default max MCP endpoint URL size in bytes. Pre-signed URLs can be long. Override via MCP_CONNECTION_URL_MAX_BYTES env var. */
export const DEFAULT_MCP_CONNECTION_URL_MAX_BYTES = 2048;
/** Default max MCP bearer token size in bytes. Override via MCP_CONNECTION_TOKEN_MAX_BYTES env var. */
export const DEFAULT_MCP_CONNECTION_TOKEN_MAX_BYTES = 8 * 1024;
/** Default max custom headers per MCP server. Override via MAX_MCP_CONNECTION_HEADERS env var. */
export const DEFAULT_MAX_MCP_CONNECTION_HEADERS = 10;
/** Default max size in bytes of one MCP custom header value. Override via MCP_CONNECTION_HEADER_VALUE_MAX_BYTES env var. */
export const DEFAULT_MCP_CONNECTION_HEADER_VALUE_MAX_BYTES = 8 * 1024;
//# sourceMappingURL=defaults.js.map